/**
 * Agnes Video V2.0 Adapter
 *
 * Asynchronous task-based API:
 * 1. POST https://apihub.agnes-ai.com/v1/videos (create task)
 * 2. GET https://apihub.agnes-ai.com/agnesapi?video_id=<VIDEO_ID> (get result)
 *
 * Model: agnes-video-v2.0
 * Features: text-to-video, image-to-video, multi-image video, keyframe animation
 *
 * API docs: https://agnes-ai.com/doc/agnes-video-v20
 */

import type {
  VideoGenerationConfig,
  VideoGenerationOptions,
  VideoGenerationResult,
  VideoProviderConfig,
} from '../types';

const DEFAULT_MODEL = 'agnes-video-v2.0';
const DEFAULT_BASE_URL = 'https://apihub.agnes-ai.com/v1';
const DEFAULT_POLL_BASE = 'https://apihub.agnes-ai.com';
const POLL_INTERVAL_MS = 5000;
const MAX_POLL_COUNT = 120; // max 10 minutes

export const AGNES_VIDEO_PROVIDERS: Record<string, VideoProviderConfig> = {
  agnes: {
    id: 'agnes',
    name: 'Agnes Video',
    requiresApiKey: true,
    defaultBaseUrl: DEFAULT_BASE_URL,
    models: [
      { id: DEFAULT_MODEL, name: 'Agnes Video V2.0' },
    ],
    supportedAspectRatios: ['16:9', '4:3', '1:1', '9:16'],
    supportedDurations: [5, 10, 15],
    supportedResolutions: ['720p', '1080p'],
    maxDuration: 15,
  },
};

interface AgnesVideoTaskResponse {
  video_id: string;
  task_id: string;
  status: string;
}

interface AgnesVideoResultResponse {
  code: number;
  data: {
    status: string;
    video_url?: string;
    video_id?: string;
    progress?: number;
  };
}

export async function testAgnesVideoConnectivity(
  config: VideoGenerationConfig,
): Promise<{ success: boolean; message: string }> {
  const baseUrl = config.baseUrl || DEFAULT_BASE_URL;
  try {
    const response = await fetch(`${baseUrl}/videos`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${config.apiKey}`,
      },
      body: JSON.stringify({
        model: config.model || DEFAULT_MODEL,
        prompt: 'test',
      }),
    });
    if (response.status === 401 || response.status === 403) {
      const text = await response.text();
      return { success: false, message: `Agnes Video auth failed (${response.status}): ${text}` };
    }
    return { success: true, message: 'Connected to Agnes Video' };
  } catch (err) {
    return { success: false, message: `Agnes Video connectivity error: ${err}` };
  }
}

export async function generateWithAgnesVideo(
  config: VideoGenerationConfig,
  options: VideoGenerationOptions,
): Promise<VideoGenerationResult> {
  const baseUrl = config.baseUrl || DEFAULT_BASE_URL;

  // Step 1: Create task
  const response = await fetch(`${baseUrl}/videos`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${config.apiKey}`,
    },
    body: JSON.stringify({
      model: config.model || DEFAULT_MODEL,
      prompt: options.prompt,
      duration: options.duration || 10,
      aspect_ratio: options.aspectRatio || '16:9',
    }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Agnes Video task creation failed (${response.status}): ${text}`);
  }

  const taskData: AgnesVideoTaskResponse = await response.json();
  const videoId = taskData.video_id;
  if (!videoId) {
    throw new Error('Agnes Video returned no video_id');
  }

  // Step 2: Poll for result
  const pollBaseUrl = config.baseUrl?.replace('/v1', '') || DEFAULT_POLL_BASE;
  let attempts = 0;
  while (attempts < MAX_POLL_COUNT) {
    attempts++;
    await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));

    const statusResponse = await fetch(`${pollBaseUrl}/agnesapi?video_id=${videoId}`, {
      headers: {
        Authorization: `Bearer ${config.apiKey}`,
      },
    });

    if (!statusResponse.ok) {
      continue;
    }

    const statusData: AgnesVideoResultResponse = await statusResponse.json();
    if (statusData.data) {
      const { status, video_url, progress } = statusData.data;
      if (status === 'completed' && video_url) {
        return {
          url: video_url,
          duration: options.duration || 10,
          width: 1280,
          height: 720,
        };
      }
    }
  }

  throw new Error('Agnes Video generation timed out after 10 minutes');
}
